import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate, useBlocker } from 'react-router'
import { Loader2 } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TransactionForm } from '@/components/transaction/transaction-form'
import type { ItemRow } from '@/components/transaction/transaction-form'
import { useProfiles, useCategories, useTransaction, useSuggestions, useUpdateTransaction, useTags, useCreateTag } from '@/lib/hooks'
import { validateAndToast, updateTransactionSchema } from '@/lib/schemas'

export default function TransactionEditPage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const month = searchParams.get('month') || undefined

  const { data: profiles = [] } = useProfiles()
  const { data: categories = [] } = useCategories()
  const { data: tags = [] } = useTags()
  const createTagMutation = useCreateTag()
  const { storeNames, itemNames } = useSuggestions()

  const { data: transaction = null, isLoading: loading } = useTransaction(id)

  const updateTx = useUpdateTransaction(month)

  const [initialized, setInitialized] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editDate, setEditDate] = useState('')
  const [editStoreName, setEditStoreName] = useState('')
  const [editItems, setEditItems] = useState<ItemRow[]>([])
  const [editPaidBy, setEditPaidBy] = useState('')
  const [editScope, setEditScope] = useState<'household' | 'personal'>('household')
  const [editMemo, setEditMemo] = useState('')
  const [editTagIds, setEditTagIds] = useState<string[]>([])
  const [editTaxAmount, setEditTaxAmount] = useState(0)

  // Initialize edit state from transaction data once loaded
  useEffect(() => {
    if (!transaction || initialized) return
    setEditDate(transaction.date)
    setEditStoreName(transaction.store_name || '')
    setEditMemo(transaction.memo || '')
    setEditItems(
      transaction.items.map((item) => ({
        id: item.id,
        name: item.name,
        amount: item.amount,
        category_id: item.category_id,
      }))
    )
    setEditPaidBy(transaction.paid_by)
    setEditScope(transaction.scope)
    setEditTagIds(transaction.tags.map(t => t.id))
    setEditTaxAmount(transaction.tax_amount ?? 0)
    setInitialized(true)
  }, [transaction, initialized])

  const markDirty = <T,>(setter: (v: T) => void) => (v: T) => {
    setDirty(true)
    setter(v)
  }

  const blocker = useBlocker(dirty && !saving)

  const handleSave = () => {
    if (updateTx.isPending) return
    if (!id) return

    const validItems = editItems.filter((i) => i.name.trim() !== '')
    const totalAmount = validItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) + editTaxAmount

    const data = validateAndToast(updateTransactionSchema, {
      paid_by: editPaidBy,
      date: editDate,
      store_name: editStoreName || undefined,
      memo: editMemo || null,
      total_amount: totalAmount,
      items: validItems.map((item) => ({
        name: item.name,
        amount: Number(item.amount) || 0,
        category_id: item.category_id || null,
      })),
      scope: editScope,
      tag_ids: editTagIds,
      tax_amount: editTaxAmount,
    })

    if (!data) return

    setSaving(true)
    updateTx.mutate(
      { id, data },
      {
        onSuccess: () => {
          const params = month ? `?month=${month}` : ''
          navigate(`/transactions/${id}${params}`, { replace: true })
        },
      },
    )
  }

  const handleCancel = () => {
    const params = month ? `?month=${month}` : ''
    navigate(`/transactions/${id}${params}`, { replace: true })
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-lg space-y-4 p-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-24" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-5 w-24" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border px-3 py-2">
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-4 w-14" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!transaction) {
    return (
      <div className="mx-auto max-w-lg p-4">
        <p className="py-16 text-center text-sm text-muted-foreground">
          取引が見つかりませんでした
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-lg space-y-4 p-4">
      <Dialog open={blocker.state === 'blocked'} onOpenChange={(open) => { if (!open) blocker.reset?.() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>編集を破棄しますか？</DialogTitle>
            <DialogDescription>
              変更内容が保存されていません。このまま戻ると編集内容が失われます。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => blocker.reset?.()}>
              編集を続ける
            </Button>
            <Button variant="destructive" onClick={() => blocker.proceed?.()}>
              破棄する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>取引を編集</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TransactionForm
            date={editDate}
            storeName={editStoreName}
            memo={editMemo}
            items={editItems}
            paidBy={editPaidBy}
            profiles={profiles}
            categories={categories}
            scope={editScope}
            taxAmount={editTaxAmount}
            tags={tags}
            selectedTagIds={editTagIds}
            storeNameSuggestions={storeNames}
            itemNameSuggestions={itemNames}
            onDateChange={markDirty(setEditDate)}
            onStoreNameChange={markDirty(setEditStoreName)}
            onMemoChange={markDirty(setEditMemo)}
            onItemsChange={markDirty(setEditItems)}
            onPaidByChange={markDirty(setEditPaidBy)}
            onTaxAmountChange={markDirty(setEditTaxAmount)}
            onScopeChange={markDirty(setEditScope)}
            onTagIdsChange={markDirty(setEditTagIds)}
            onCreateTag={(data) => createTagMutation.mutate(data)}
          />

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCancel}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={handleSave}
              disabled={updateTx.isPending}
            >
              {updateTx.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              保存
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
